import { DEFAULT_STATUS } from "@/runs/status"

import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRow, RunRowLike } from "@/types/RunRow"
import { toISODateString } from "@/utils/date/date-range-helpers"
import { downloadBlob } from "@/utils/download"

import {
  COMPARE_BY_COLUMN_KEY,
  type RunResultsExplanationTable,
} from "../../hooks/useRunResultsColumnDefs"
import type { RunResultsTableColumn } from "../../hooks/useRunResultsTableColumns"
import type { RunResultsMetricRenderAdaptersById } from "../../types"

export interface BuildRunResultsCsvParams {
  allColumns: RunResultsTableColumn[]
  columnOrder: string[]
  compareColumnLabel: string | null
  explanationTable: RunResultsExplanationTable
  getCompareDisplayValue: (row: RunRowLike) => string
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  mutationNameById: ReadonlyMap<string, string>
  rows: RunRowLike[]
  visibleColumns: ReadonlySet<string>
}

interface RunResultsCsv {
  columnCount: number
  content: string
}

function getTranscriptPreview(row: RunRowLike): string {
  return (
    (row as Partial<Pick<RunRow, "transcript">>).transcript?.[0]?.content ?? ""
  )
}

function escapeCsvValue(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`
  }

  return value
}

function getMetricValue(
  row: RunRowLike,
  columnKey: string,
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById,
): string {
  const metricId = metricRenderAdaptersById[columnKey].id
  const metricValue: unknown = row.metric_outputs?.[metricId]
  if (!metricValue) {
    return ""
  }
  if (typeof metricValue !== "object") {
    return String(metricValue as string | number | boolean | bigint)
  }

  const metricOutput = metricValue as RunMetricOutput

  if (metricOutput.output_type === "float") {
    return metricOutput.float_value?.toString() || ""
  }
  if (metricOutput.output_type === "string") {
    return metricOutput.string_value || ""
  }
  if (metricOutput.output_type === "set") {
    return metricOutput.set_value?.join(", ") || ""
  }

  return ""
}

function getCellValue(
  row: RunRowLike,
  rowIndex: number,
  columnKey: string,
  params: BuildRunResultsCsvParams,
): string {
  switch (columnKey) {
    case "index":
      return (rowIndex + 1).toString()
    case "errorStatus":
      return row.error_status || DEFAULT_STATUS
    case "simulationOutputId":
      return row.simulation_output_id || ""
    case "mutationName":
      return (
        row.mutation_name ??
        (row.mutation_id
          ? params.mutationNameById.get(row.mutation_id)
          : null) ??
        ""
      )
    case "inputStr":
      return row.input_str || ""
    case "expectedBehaviors":
      return row.expected_behaviors.join(" • ") || ""
    case "expectedOutputJson":
      return row.expected_output_json
        ? JSON.stringify(row.expected_output_json)
        : ""
    case "transcript": {
      const normalizedErrorStatus = row.error_status?.toUpperCase()
      const hasError =
        normalizedErrorStatus?.includes("ERROR") ||
        normalizedErrorStatus?.includes("FAILED")
      if (hasError && row.error_message) {
        return `Error: ${row.error_message.replace(/vapi/gi, "")}`
      }
      if (row.simulation_s3_bucket && row.simulation_s3_key) {
        return "Audio file"
      }
      return getTranscriptPreview(row)
    }
    default:
      if (columnKey === COMPARE_BY_COLUMN_KEY && params.compareColumnLabel) {
        return params.getCompareDisplayValue(row)
      }
      if (
        params.explanationTable.columns.some(
          (column) => column.key === columnKey,
        )
      ) {
        return params.explanationTable.getRowValues(row)[columnKey] ?? ""
      }
      if (columnKey in params.metricRenderAdaptersById) {
        return getMetricValue(row, columnKey, params.metricRenderAdaptersById)
      }
      return ""
  }
}

export function buildRunResultsCsv(
  params: BuildRunResultsCsvParams,
): RunResultsCsv {
  const visibleColumnOrder = params.columnOrder.filter((columnKey) =>
    params.visibleColumns.has(columnKey),
  )
  const headers = visibleColumnOrder.map((columnKey) => {
    const column = params.allColumns.find(
      (candidate) => candidate.key === columnKey,
    )
    return column?.label || columnKey
  })
  const csvRows = params.rows.map((row, rowIndex) =>
    visibleColumnOrder.map((columnKey) =>
      escapeCsvValue(getCellValue(row, rowIndex, columnKey, params)),
    ),
  )

  return {
    columnCount: headers.length,
    content: [headers, ...csvRows].map((csvRow) => csvRow.join(",")).join("\n"),
  }
}

export function downloadRunResultsCsv(
  params: BuildRunResultsCsvParams,
): RunResultsCsv {
  const csv = buildRunResultsCsv(params)
  const blob = new Blob([csv.content], { type: "text/csv;charset=utf-8;" })
  downloadBlob(blob, `run-results-export-${toISODateString()}.csv`)
  return csv
}
